import { Link as RouterLink, useSearchParams } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Button, Typography, Container, Box, Link, Stack, Card } from '@mui/material';
// components
import { useForm } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import { useEffect } from 'react';
import Page from '../components/page';
import Logo from '../components/logo/Logo';
import Banner from '../components/banner';
// config
import config from '../config';
// hooks
import useFormStateUpdateOnChange from '../hooks/useFormStateUpdateOnChange';
import useResponsive from '../hooks/useResponsive';

// ----------------------------------------------------------------------

const CoverStyle = styled(Card)(({ theme }) => ({
  width: '50%',
  zIndex: -1,
  maxWidth: 1000,
  margin: theme.spacing(2, 0, 2, 2),
  background: `url('/assets/covers/homePage.jpg'), linear-gradient(rgba(0,0,0,0.0),rgba(0,0,0,0.1))`,
  backgroundBlendMode: 'overlay',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

const ContentWrapper = styled(Stack)(({ theme }) => ({
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

const { contactEmail, title, header } = config;

export default function HomePage() {
  const lgUp = useResponsive('up', 'lg');
  const smUp = useResponsive('up', 'sm');

  document.title = title;

  const [searchParams] = useSearchParams();

  // state
  const { state } = useStateMachine();

  // form
  const methods = useForm({
    mode: 'onChange',
    defaultValues: state.form,
  });

  const { watch } = methods;

  useFormStateUpdateOnChange(watch);


  useEffect(() => {
    if(searchParams.size > 0) {

      const applicantName = searchParams.get('applicantName');
      const phone = searchParams.get('phone');
      const insuredName = searchParams.get('insuredName');
      const insuredAddress = searchParams.get('insuredAddress');
      const insuredCity = searchParams.get('insuredCity');
      const insuredState = searchParams.get('insuredState');
      const insuredZip = searchParams.get('insuredZip');

      
      if(applicantName) {
        methods.setValue('policyOwnershipAndFraudStatement.applicantName', applicantName);
      }
    
      if(phone) {
        methods.setValue('policyOwnershipAndFraudStatement.phone', phone);
      }
    
      if(insuredName) {
        methods.setValue('insured.name', insuredName);
      }
    
      if(insuredAddress) {
        methods.setValue('insured.streetAddress', insuredAddress);
      }
    
      if(insuredCity) {
        methods.setValue('insured.city', insuredCity);
      }
    
      if(insuredState) {
        methods.setValue('insured.state', insuredState);
      }
    
      if(insuredZip) {
        methods.setValue('insured.zip', insuredZip);
      }

      if(insuredAddress || insuredCity || insuredState || insuredZip) {
        const mailingAddress = [
          insuredAddress,
          insuredCity,
          [insuredState, insuredZip].filter(x => x).join(' '),
        ].filter(x => x).join(', ');
        methods.setValue('policyOwnershipAndFraudStatement.mailingAddress', mailingAddress);
      }
    }
  }, []);

  return (
    <Page title={title}>
      <Box display={'flex'} minHeight={'100vh'}>
        {lgUp && <CoverStyle />}
        <Container maxWidth="md">
          <ContentWrapper spacing={2}>
            {smUp && <Logo width={'100%'} sx={{ maxWidth: '350px', margin: '0 auto' }} />}

            <Banner type={'info'} sx={{ p: { xl: 2 } }}>
              <Typography variant="h3" component={'h1'}>
                {header}
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography>Before starting your application, please have the following documents ready:</Typography>
                </Box>
                <Box pl={2}>
                  <Typography>
                    Loss Runs (past insurance experience, if applicable 3-5 years – not needed for new DOT authorities)
                  </Typography>
                  <Typography>
                  Estimated annual payroll by class code (i.e. driver, clerical, warehouse, etc.)
                  </Typography>
                </Box>

                <Typography>
                  If you have trouble uploading any requested documents, please email them to{' '}
                  <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>
                </Typography>
                <Typography>
                  McGriff will reach out to you after we receive your application and advise you of any information
                  needed. We cannot provide a quote for incomplete applications.
                </Typography>
                <Typography>
                  NOTE: Not available in monopolistic states (Ohio, North Dakota, Washington, and Wyoming)
                </Typography>
              </Stack>
              <Stack direction={'row'} spacing={2} mt={2} alignItems={'center'} justifyContent={'flex-end'}>
                <Button to="/form" size="large" variant="outlined" component={RouterLink}>
                  Apply Now
                </Button>
              </Stack>
            </Banner>
          </ContentWrapper>
        </Container>
      </Box>
    </Page>
  );
}
